import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export interface SEOProps {
  /** Page title - will be appended with site name */
  title?: string;
  /** Meta description for search results */
  description?: string;
  /** Canonical URL path (without domain) */
  canonicalPath?: string;
  /** Open Graph image URL */
  ogImage?: string;
  /** Open Graph type */
  ogType?: "website" | "article" | "product" | "service";
  /** Keywords for the page */
  keywords?: string[];
  /** Whether to prevent indexing */
  noIndex?: boolean;
  /** Structured data (JSON-LD) */
  structuredData?: object;
  /** Article publication date (for blog posts) */
  publishedTime?: string;
  /** Article modified date */
  modifiedTime?: string;
}

const SITE_NAME = "WIH Media";
const SITE_URL = "https://wihmedia.com";
const DEFAULT_DESCRIPTION =
  "Professional real estate photography, FAA Part 107 certified aerial drone photography, and software solutions in Indian River County, Florida.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_KEYWORDS = [
  "real estate photography",
  "aerial photography",
  "drone photography",
  "Vero Beach",
  "Indian River County",
  "Florida",
  "FAA Part 107",
  "property photography",
  "real estate marketing",
];

/**
 * SEO component that manages document head meta tags
 * Uses useEffect to update meta tags when props change
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  keywords = DEFAULT_KEYWORDS,
  noIndex = false,
  structuredData,
  publishedTime,
  modifiedTime,
}) => {
  const location = useLocation();
  const currentPath = canonicalPath || location.pathname;
  const fullUrl = `${SITE_URL}${currentPath}`;
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} - Real Estate Photography & Aerial Services`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper function to update or create meta tags
    const setMetaTag = (attribute: "name" | "property", key: string, content: string) => {
      let element = document.querySelector(`meta[${attribute}="${key}"]`);
      if (element) {
        element.setAttribute("content", content);
      } else {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        element.setAttribute("content", content);
        document.head.appendChild(element);
      }
    };

    // Helper function to update or create link tags
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (element) {
        element.setAttribute("href", href);
      } else {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        element.setAttribute("href", href);
        document.head.appendChild(element);
      }
    };

    // Basic meta tags
    setMetaTag("name", "description", description);
    setMetaTag("name", "keywords", keywords.join(", "));
    setMetaTag("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph meta tags
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", fullUrl);
    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:site_name", SITE_NAME);
    setMetaTag("property", "og:locale", "en_US");

    // Twitter Card meta tags
    setMetaTag("property", "twitter:card", "summary_large_image");
    setMetaTag("property", "twitter:title", fullTitle);
    setMetaTag("property", "twitter:description", description);
    setMetaTag("property", "twitter:image", ogImage);
    setMetaTag("property", "twitter:url", fullUrl);

    // Article-specific meta tags
    if (publishedTime) {
      setMetaTag("property", "article:published_time", publishedTime);
    }
    if (modifiedTime) {
      setMetaTag("property", "article:modified_time", modifiedTime);
    }

    // Canonical URL
    setLinkTag("canonical", fullUrl);

    // Structured data
    if (structuredData) {
      // Remove existing structured data for this component
      const existingScript = document.querySelector('script[data-seo-component="true"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-component", "true");
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function to remove dynamically added structured data
    return () => {
      const script = document.querySelector('script[data-seo-component="true"]');
      if (script) {
        script.remove();
      }
    };
  }, [
    fullTitle,
    description,
    fullUrl,
    ogImage,
    ogType,
    keywords,
    noIndex,
    structuredData,
    publishedTime,
    modifiedTime,
  ]);

  return null; // This component doesn't render anything
};

export default SEO;
